/**
 * Surfaces & type — from the Dashboard, AI Briefing and Session Setup (see `kit.ts` for the canonical sources).
 * Each component emits the reference literal verbatim, in its declaration order.
 */
import type { CSSProperties, ReactNode } from 'react';
import { css, styleOf } from './style';

/* ── Card (dashboard 253, briefing 736, session setup 807) ──────────────────────────────────────────────────── */
export const CARD_BASE =
  'background:var(--surface);border:1px solid var(--card-line);box-shadow:var(--shadow-card);border-radius:16px';
/** The paddings the canonical surfaces use. `tall` (18px 20px) is Session Setup's and Briefing's; `dense` is the Dashboard's. */
export const CARD_PADDING = {
  dense: 'padding:14px 18px 12px', // dashboard 253, 314
  roomy: 'padding:16px 18px 14px', // dashboard 396
  flat: 'padding:16px 18px', // briefing 761, 774
  tall: 'padding:18px 20px', // session setup 807; briefing 736
  compact: 'padding:13px 15px',
  list: 'padding:14px 16px',
  none: '',
} as const;
export type CardPadding = keyof typeof CARD_PADDING;
export interface CardProps {
  padding?: CardPadding;
  /** Declarations appended after the surface, exactly as the reference appends layout. */
  extra?: string;
  /** Session Setup's full-width card starts with `grid-column:1/-1` before the surface (line 812). */
  fullWidth?: boolean;
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
}
export function Card({ padding = 'flat', extra, fullWidth, style, className, children }: CardProps) {
  const lit = [fullWidth ? 'grid-column:1/-1' : '', CARD_BASE, CARD_PADDING[padding], extra]
    .filter(Boolean)
    .join(';');
  return (
    <div className={className} style={style ? { ...css(lit), ...style } : css(lit)}>
      {children}
    </div>
  );
}

/* ── Inset panels ───────────────────────────────────────────────────────────────────────────────────────────── */
/** Briefing's inset (line 745, ×4): a hairline box, no fill. */
export const INSET_OUTLINE = 'border:1px solid var(--l8);border-radius:10px;padding:13px 15px';
/** Session Setup's inset (line 869, ×2): hairline + the `--l3` fill. Radius 11 per the package's design language. */
export const INSET_FILLED =
  'border:1px solid var(--l8);border-radius:11px;background:var(--l3);padding:11px 13px';
export interface InsetProps {
  filled?: boolean;
  extra?: string;
  style?: CSSProperties;
  children?: ReactNode;
}
export function Inset({ filled = false, extra, style, children }: InsetProps) {
  const lit = (filled ? INSET_FILLED : INSET_OUTLINE) + (extra ? ';' + extra : '');
  return <div style={style ? { ...css(lit), ...style } : css(lit)}>{children}</div>;
}
/** Back-compat alias — the filled inset. */
export const InsetPanel = ({
  extra,
  style,
  children,
}: {
  extra?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) => (
  <Inset filled extra={extra} style={style}>
    {children}
  </Inset>
);

/* ── Type (dashboard 256, session setup 815, briefing 741) ──────────────────────────────────────────────────── */
/** Section label — the dashboard's card kicker (×8). */
export const SECTION_LABEL =
  'font-size:9.5px;letter-spacing:0.13em;text-transform:uppercase;color:var(--muted2);font-weight:600';
export function SectionLabel({ children, extra }: { children?: ReactNode; extra?: string }) {
  return <div style={styleOf(SECTION_LABEL, extra)}>{children}</div>;
}
/** Field label — Session Setup's form caption (×4 with `white-space:nowrap`, ×3 without). */
export const FIELD_LABEL =
  'font-size:9px;letter-spacing:0.11em;text-transform:uppercase;color:var(--muted2);font-weight:600';
export function FieldLabel({
  children,
  nowrap,
  extra,
}: {
  children?: ReactNode;
  nowrap?: boolean;
  extra?: string;
}) {
  return <div style={styleOf(FIELD_LABEL + (nowrap ? ';white-space:nowrap' : ''), extra)}>{children}</div>;
}
/** Alias kept for the figure-caption role. */
export const MicroLabel = FieldLabel;

/** Card title with its mode-accent icon — Session Setup (line 808, ×4). */
export interface CardTitleProps {
  icon?: string;
  iconColor?: string;
  children?: ReactNode;
  extra?: string;
}
export function CardTitle({ icon, iconColor = 'var(--amb)', children, extra }: CardTitleProps) {
  return (
    <div style={styleOf('display:flex;align-items:center;gap:8px;font-size:13px;font-weight:500', extra)}>
      {icon ? <i className={`ph ${icon}`} style={css(`color:${iconColor};font-size:16px`)}></i> : null}
      {children}
    </div>
  );
}
/** Card subtitle — Session Setup (line 809, ×6). */
export function CardSubtitle({ children, extra }: { children?: ReactNode; extra?: string }) {
  return <div style={styleOf('font-size:11.5px;color:var(--muted);margin-top:5px', extra)}>{children}</div>;
}
/** Muted body note — Briefing (line 747, ×4). */
export function MutedNote({ children, extra }: { children?: ReactNode; extra?: string }) {
  return (
    <div style={styleOf('font-size:11.5px;color:var(--muted);line-height:1.6;margin-top:7px', extra)}>
      {children}
    </div>
  );
}
/** Reading body — Briefing's brief text (line 612). */
export function BodyText({ children, extra }: { children?: ReactNode; extra?: string }) {
  return (
    <div style={styleOf('font-size:12.5px;line-height:1.65;color:var(--text2);max-width:78ch', extra)}>
      {children}
    </div>
  );
}

/* ── Rules & layout ─────────────────────────────────────────────────────────────────────────────────────────── */
/** Section rule — the fading gradient rule (briefing 611). */
export function SectionRule({ extra }: { extra?: string }) {
  return (
    <div
      style={styleOf(
        'height:1px;margin:12px 0;background:linear-gradient(90deg,transparent,var(--l10) 48px,var(--l10) calc(100% - 48px),transparent)',
        extra,
      )}
    ></div>
  );
}
/** Flat divider — the account menu's (header bar 233, ×2). */
export function Divider({ extra }: { extra?: string }) {
  return <div style={styleOf('height:1px;background:var(--l8);margin:12px 0', extra)}></div>;
}
/** Footer/legend row — the design language's `padding-top:9px;border-top:1px solid var(--l6)`. */
export function FooterRow({ children, extra }: { children?: ReactNode; extra?: string }) {
  return (
    <div
      style={styleOf(
        'display:flex;align-items:center;gap:8px;padding-top:9px;margin-top:11px;border-top:1px solid var(--l6);font-size:11px',
        extra,
      )}
    >
      {children}
    </div>
  );
}
/** Screen shell — Briefing's centred column (line 731) and Session Setup's 3-column grid (line 802). */
export function Screen({
  layout = 'column',
  children,
}: {
  layout?: 'column' | 'grid3' | 'auto';
  children?: ReactNode;
}) {
  const lit =
    layout === 'grid3'
      ? 'max-width:1440px;margin:0 auto;padding:24px 12px 40px;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;align-items:stretch'
      : layout === 'auto'
        ? 'max-width:1440px;margin:0 auto;padding:24px 12px 40px;display:grid;grid-template-columns:repeat(auto-fit,minmax(420px,1fr));gap:12px;align-items:start'
        : 'max-width:1440px;margin:0 auto;padding:24px 12px 40px;display:flex;flex-direction:column;gap:16px';
  return <div style={css(lit)}>{children}</div>;
}
